import type { InterestedLead, PendingApplication } from '../components/waitlist/types';
import { TagInfo } from '../components/waitlist/utils';

export interface WaitlistLead {
  id: string;
  name: string;
  email: string;
  phone?: string;
  tags: (TagInfo | string)[]; // Support both TagInfo and string for backward compatibility
  daysOnWaitlist: number;
  floorPlan?: string;
  dateAdded: string;

  accessibility?: string;
  notes?: string;
  status: WaitlistStatus;

  // Additional raw API fields for complete data access
  rank?: string;
  applicantId?: string;
  contactNumber?: string | null;
  applicationStageId?: string;
  applicationStatusId?: string;
  floorplans?: string;
  leaseIntervalId?: string;
  leaseId?: string | null;
  customerId?: string;
  allApplicants?: string;
  waitListId?: string;
  floorPlanNames?: string[];
  floorPlanIds?: number[];
  spaceOptionNames?: string[];
  spaceConfigurationIds?: number[];
  floorplanDateAdded?: string[];
}

export enum WaitlistStatus {
  ACTIVE = 'active',
  CONTACTED = 'contacted',
  SCHEDULED = 'scheduled',
  CONVERTED = 'converted',
  LOST = 'lost',
  URGENT = 'urgent'
}

export interface WaitlistFilters {
  status?: WaitlistStatus[];
  tags?: string[];
  floorPlan?: string[];
  searchTerm?: string;
}

export interface FilterData {
  // Property information - store both ID and name for backend integration
  property: string; // Property ID (for backward compatibility)
  propertyId?: number; // Numeric property ID for API calls
  propertyName?: string; // Property name for display
  areaUnitLabel?: string; // Property-configured area unit for display

  // Occupancy type - already stores ID
  occupancyType: number; // Occupancy type ID
  occupancyTypeName?: string; // Occupancy type name for display

  // Lease term information - store both ID and name
  leaseTerm: string; // Lease term ID (for backward compatibility)
  leaseTermId?: number; // Numeric lease term ID for API calls
  leaseTermName?: string; // Lease term name for display

  // Lease start window information - needed for PUT requests
  leaseStartWindowId?: number; // Lease start window ID for API calls
  leaseStartWindowName?: string; // Lease start window name for display

  // Floor plan preferences - store both ID and name for backend integration
  floorplanPreference: string[]; // Floorplan names for display (backward compatibility)
  floorplanIds?: number[]; // Floorplan IDs for API calls
  floorplanNames?: string[]; // Floorplan names for display (explicit)

  // Space options - store both ID and name for backend integration
  spaceOptions: string[]; // Space option names for display (backward compatibility)
  spaceOptionIds?: number[]; // Space option IDs for API calls (should be numbers)
  spaceOptionNames?: string[]; // Space option names for display (explicit)

  // Application stage status filter - NEW
  application_stage_status?: string; // Comma-separated IDs for PUT API requests
  applicationStageStatusIds?: number[]; // Array of selected status IDs
  applicationStageStatusNames?: string[]; // Array of selected status names for display

  // Date filters - NEW
  from_date?: string; // From date for filtering (YYYY-MM-DD format)
  to_date?: string; // To date for filtering (YYYY-MM-DD format)

  // Affordable Program Type (only for Affordable occupancy)
  affordableProgramType?: string;
  affordableProgramTypeName?: string;

  // Subsidy ID (for offered applications API)
  subsidyId?: number;
  allSubsidyTypeIds?: string;
}

export interface ChartData {
  floor_plan: string;
  app_count: number;
  available_space_count: number;
}

// Dashboard Graph Data types
/**
 * Floorplan data with waitlist and availability information
 * Based on actual API response from get_floorplans_with_available_spaces endpoint
 */
export interface FloorplanWaitlistCount {
  // Primary fields from API response (camelCase)
  propertyFloorplanId: number;
  floorplanName: string;
  waitlistApplicationCount: number;
  availableUnitSpacesCount: number;
  propertyId: number;

  // Additional fields from API response
  totalUnitSpacesCount?: number;
  bedCount?: number;
  bathCount?: number;
  squareFeet?: number;
  areaUnitLabel?: string;
  minRent?: number;
  maxRent?: number;

  // Legacy field names for backward compatibility (snake_case)
  property_floorplan_id?: number;
  floorplan_name?: string;
  waitlist_application_count?: number;
  available_space_count?: number;
  property_id?: number;
}

/**
 * API response structure for dashboard graph data
 * Endpoint: get_floorplans_with_available_spaces
 *
 * Example response:
 * {
 *   "status": "success",
 *   "data": [
 *     {
 *       "propertyFloorplanId": 159435,
 *       "floorplanName": "1BHK",
 *       "waitlistApplicationCount": 55,
 *       "availableUnitSpacesCount": 0,
 *       "propertyId": 58021,
 *       "totalUnitSpacesCount": 0,
 *       "bedCount": 0,
 *       "bathCount": 0,
 *       "squareFeet": 0,
 *       "minRent": 0,
 *       "maxRent": 0
 *     }
 *   ]
 * }
 */
export interface DashboardGraphDataResponse {
  status: string;
  message?: string;
  data: FloorplanWaitlistCount[];
}

export enum ChartSortOption {
  FLOOR_PLAN = 'floor_plan',
  WAITLIST_COUNT = 'waitlist_count',
  SPACES_AVAILABLE = 'spaces_available'
}

export interface UIState {
  isFilterPopupOpen: boolean;
  activeTab: number;
  columnVisibility: ColumnVisibility;
  isDashboardVisible: boolean;
  chartSortBy: ChartSortOption;
}

export interface ColumnVisibility {
  name: boolean;
  tags: boolean;
  daysOnWaitlist: boolean;
  floorPlan: boolean;
  dateAdded: boolean;
  status: boolean;
  accessibility: boolean;
  notes: boolean;
  skipReason: boolean;
  refusals: boolean;
  actions: boolean;
}

export interface WaitlistStats {
  totalLeads: number;
  urgentLeads: number;
  conversionRate: number;
  averageDaysOnWaitlist: number;
}

// Bulk Selection Types
export interface BulkAction {
  id: string;
  label: string;
  icon?: string;
  color?: string;
  requiresConfirmation?: boolean;
}

export enum BulkActionType {
  SEND_EMAIL = 'send_email',
  SEND_SMS = 'send_sms',
  ASSIGN_TAGS = 'assign_tags',
  SEND_APPLICATION = 'send_application',
  ARCHIVE_LEADS = 'archive_leads',
  REMOVE_FROM_WAITLIST = 'remove_from_waitlist'
}

export interface BulkSelectionState {
  selectedLeadIds: string[];
  isAllSelected: boolean;
  isIndeterminate: boolean;
}

// Tag Management Types
export interface Tag {
  id: string;
  name: string;
  color: string;
  description?: string;
  isActive: boolean;
  isSystem: boolean;
  rank?: number;
  createdAt: string;
  updatedAt: string;
}

export interface TagColor {
  name: string;
  value: string;
  displayName: string;
}

export interface PropertyTagManagementState {
  tags: Tag[];
  isLoading: boolean;
  selectedProperty: string;
  selectedOccupancy: number;
  modifiedTagIds: string[]; // Track which tags have been modified
}

export interface Note {
  id: number;
  cid: number;
  application_id: number;
  event_id: number | null;
  text: string;
  pinned: boolean | number | string;
  created_by: number;
  created_on: string;
  updated_by: number;
  updated_on: string;
  deleted_by: number | null;
  deleted_on: string | null;
  author: string;
}

export interface NotesApiResponse {
  status: string;
  data: {
    application_id: number;
    notes: Note[];
  };
}

export interface WaitlistEvent {
  event_type_id: string;
  activity: string;
  date_time: string;
  user: string;
}

export interface WaitlistEventsApiResponse {
  status: string;
  data: {
    application_id: number;
    events: WaitlistEvent[];
  };
}
// Waitlist Application API Interfaces
export interface WaitlistApplication {
  rank: string;
  application_id: string;
  applicant_name: string;
  date_added: string;
  applicant_id: string;
  contact_number: string | null;
  email_address: string | null;
  application_stage_id: string;
  application_status_id: string;
  floorplans: string;
  lease_interval_id: string;
  lease_id: string | null;
  customer_id: string;
  status_name: string;
  notes: string | null;
  waitlist_notes?: string | null;
  all_applicants: string;
  tags: string | null;
  wait_list_id: string;
  waitlist_tag_ids: string | null;
  floor_plan_names: string[];
  floor_plan_ids: number[];
  space_option_names: string[];
  space_configuration_ids: number[];
  floorplan_date_added: string[];
}

// Interface for waitlist applications API response
export interface WaitlistApplicationsApiResponse {
  status: string;
  data: {
    applications: WaitlistApplication[];
    has_more: boolean;
    page_offset: number;
    page_limit: number;
    price_by_space_option_enabled: boolean;
  };
}

// Student Lease Terms API Interfaces
export interface StudentLeaseTerm {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  lease_term_id: string;
  property_id: number;
  occupancy_type_id: number;
}

export interface StudentLeaseTermsApiResponse {
  status: string;
  data: {
    message: string;
    property_id: number;
    occupancy_type_id: number;
    lease_terms: Record<string, StudentLeaseTerm>;
  };
}

// Applicant Info API Interfaces
export interface ApplicantFloorplan {
  application_floor_plan_id: number;
  floor_plan_name: string;
  property_floor_plan_id: number;
  space_configuration_id: number | null;
}

export interface ApplicantInfo {
  application_id: number;
  application_occupancy_type_id: number;
  is_waitlist_application: boolean;
  property_name: string;
  applicant_name: string;
  desired_bedrooms: number;
  desired_bathrooms: number;
  applicant_email: string;
  applicant_phone: string;
  applicant_gender: string;
  lease_id: number;
  space_configuration_name: string;
  desired_space_configuration_id: number;
  space_configuration_id: number;
  primary_applicant_id: number;
  wait_start_on: string;
  days_on_waitlist: number;
  wait_end_on: string | null;
  date_applied: string | null;
  rejection_count: number;
  floorplans: ApplicantFloorplan[];
  floorplans_count: number;
  waitlist_tags: ApplicationTag[];
}

export interface ApplicantInfoApiResponse {
  status: string;
  data: ApplicantInfo;
}

// Interface for application tag
export interface ApplicationTag {
  id: number;
  name: string;
  backgroundColor: string;
}

// Interface for updating application waitlist tags request
export interface UpdateApplicationTagsRequest {
  tagIds: number[];
}

// Interface for updating application waitlist tags response
export interface AddOrUpdateApplicationTags {
  status: string;
  data: {
    applicationId: number;
    tags: ApplicationTag[];
  };
}

export interface WaitlistFloorPlan {
  floor_plan_id: number;
  space_option_id: number;
}

interface StudentWaitlistPreferenceRequest {
  application_id: number;
  lease_term_id: number;
  lease_start_window_id: number;
  waitlist_floor_plans: WaitlistFloorPlan[];
}

interface ConventionalWaitlistPreferenceRequest {
  application_id: number;
  property_floorplan_ids: number[];
}

interface AffordableWaitlistPreferenceRequest {
  application_id: number;
  property_floorplan_ids: number[];
  date_applied?: string;
  time_applied?: string;
  waitlist_tags?: number[];
  top_priority_floor_plan?: number;
  old_top_priority_floor_plan?: number;
  application_waitlist_point_id?: number;
}

export type UpdateStudentWaitlistPreferenceRequest =
  | StudentWaitlistPreferenceRequest
  | ConventionalWaitlistPreferenceRequest
  | AffordableWaitlistPreferenceRequest;

// Interface for updating student waitlist preferences response
export interface UpdateStudentWaitlistPreferenceResponse {
  status: string;
  data: {
    application_id: number;
    message: string;
  };
}

// Add interface for save note response
export interface SaveNoteApiResponse {
  status: string;
  message: string;
}

export interface SendInvitationApiResponse {
  status: string;
  data: {
    message: string;
    data: {
      application_id: number;
      property_floorplan_id: number;
      space_configuration_id: number;
      applicant_id: number;
    };
  };
}

export interface SendOfferRequest {
  application_id: number;
  applicant_id: number;
  application_interest: {
    unit_space_id: number;
    property_floorplan_id?: number;
    space_configuration_id?: number;
    is_email_offer: boolean;
    is_sms_offer: boolean;
  };
  unit_space_available_on?: string;
  application_interest_skips?: {
    skipped_applicants?: string;
    skip_reason?: string;
  };
}

export interface SendOfferApiResponse {
  status: string;
  message: string;
  error?: {
    code: number;
    type: string;
    message: string;
  };
  data?: {
    application_id: number;
    applicant_id: number;
    offer_sent: boolean;
  };
}

export interface DeclineOfferRequest {
  application_id: number;
  decline_criteria: 'removeFromWaitlist' | 'removeFromFloorplanGroup' | 'removeFromEntireWaitlist';
  application_interest_id?: number;
  decline_reason_id?: number;
  decline_sub_reason_ids?: number[];
  decline_notes?: string;
  lead_action?: 'archive' | 'cancel';
}

export interface DeclineOfferApiResponse {
  status: string;
  message: string;
  data?: {
    application_id: number;
    declined: boolean;
  };
}

export interface AffordableCertificationApiResponse {
  status: string;
  data: {
    subsidy_project_id: string;
    subsidy_project_name: string;
    subsidy_project_number: string;
    subsidy_type_id: string;
    lease_id: number;
    customer_id: string;
    is_month_to_month_lease_interval: boolean;
    is_transfer_lease_interval_type_id: boolean;
    application_id: number;
    current_income: number;
    current_assets: number;
    current_expenses: number;
    subsidy_special_status_types: Record<string, any>;
    household_subsidy_special_status_type_ids: number[];
    eligibile_household_size: number;
    current_total_income: number;
  };
}

export interface AddNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicantName: string;
  applicantId: string;
  applicationId: number;
  onNoteAddSuccess?: (applicationId: string | number) => void;
}

// Interface for application waitlist tags response
export interface ApplicationWaitlistTagsApiResponse {
  status: string;
  data: {
    application_id: number;
    tags: ApplicationTag[];
  };
}

// Interface for pending applications API response
export interface PendingApplicationsApiResponse {
  status: string;
  data: PendingApplication[];
}

// Interface for interested applications API response
export interface InterestedApplicationsApiResponse {
  status: string;
  data: InterestedLead[];
}

// Available Applications API types (Conventional/Affordable)
export interface AvailableApplicant {
  applicationId: number;
  applicantId: number;
  applicantName: string;
  contactNumber: string | null;
  emailAddress: string | null;
  leaseStartDate: string | null;
  waitListPosition: number | null;
  waitListPoint: number | null;
  screeningRecommendationTypeId: number;
  disabilities: string | null;
  waitListTags: string | null;
  bgColors: string | null;
  offeredUnitSpaceId: number | null;
  offerSubmittedOn: string | null;
  applicationCompletedOn: string | null;
  propertyFloorplanId: number | null;
  applicationFloorplanIds: number[];
  hasActiveOffer: boolean;
}

export interface AvailableApplicationsData {
  floorplanId: number;
  floorplanName: string;
  numberOfBedrooms: number;
  numberOfBathrooms: number;
  applicants: AvailableApplicant[];
  totalCount: number;
  pageOffset: number;
  pageLimit: number;
  occupancyTypeId: number;
  subsidyTypeId: number | null;
}

export interface AvailableApplicationsApiResponse {
  status: string;
  data: AvailableApplicationsData;
}

export interface FloorPlan {
  floorplan_name: string;
  floorplan_id: number;
  space_option_name: string;
  space_configuration_id: number;
  created_on: string;
}

export interface FloorPlanOption {
  value: string;
  floorPlanName: string;
  spaceOptionName: string;
  displayText: string;
  floorplanId: number;
  spaceConfigurationId: number;
}

export interface SendApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicantName: string;
  applicantId: string;
  applicationId: string;
  floorplans: string;
}
// Interface for remove waitlist application request
export interface RemoveWaitlistApplicationRequest {
  applicationIds: number[];
}

// Interface for remove waitlist application response
export interface RemoveWaitlistApplicationResponse {
  status: string;
  data: {
    message: string;
  };
}

// Interface for floorplan with space options from student waitlist API
export interface FloorplanWithSpaceOption {
  floorplan_inventory_id: string;
  unit_type_id: string;
  inventory_status_type: string;
  space_option_id: string;
  space_option_name: string;
  floor_plan_id: string;
  floorplan_name: string;
}

// Interface for floorplan space options data
export interface FloorplanSpaceOptionsData {
  property_id: number;
  lease_start_window_id: number;
  floorplans: FloorplanWithSpaceOption[];
}

// Interface for floorplan space options API response
export interface FloorplanSpaceOptionsApiResponse {
  status: string;
  message?: string;
  data: FloorplanSpaceOptionsData;
}

// Interface for processed space option (used by the hook)
export interface SpaceOption {
  space_option_id: string;
  space_option_name: string;
}

export interface OfferedUnitsData {
  offeredUnits: Record<string, unknown>[];
  totalCount: number;
  pageOffset: number;
  pageLimit: number;
  hasMore: boolean;
}

export interface OfferedUnitsApiResponse {
  status: string;
  data: OfferedUnitsData;
}
